<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
// Overrides @basecom-gmbh/pond/components/Layout/Header/Search/LayoutHeaderSearchBar.vue
// IDS-124: change style of search bar, add bg color for md screens
// IDS-172: change style of "show all results", change default displayTotal, move on click outside in this file

import { getProductRoute } from '@shopware-pwa/helpers-next';
import type { Schemas } from '@shopware/api-client/api-types';

const props = withDefaults(
    defineProps<{
        displayTotal?: number;
        minCharacters?: number;
    }>(),
    {
        displayTotal: 6,
        minCharacters: 2,
    },
);

const emit = defineEmits(['closeSearch']);

const { searchTerm, search, getProducts, getTotal, loading } = useProductSearchSuggest();
const { trackSearchSuggestions, trackSearch, trackSelectItem } = useAnalytics();
const { url } = useCountrySwitch();

const router = useRouter();
const { t } = useI18n();

// String the user has typed in the search field
const typingQuery = ref('');

// Reference to the searchInput to focus it onMount
const searchInput = ref(null);

// Copy the search results to a local variable to be able to delete them when clicking outside
const results = ref<Schemas['Product'][]>([]);

// If the user is on the result page, hide suggestions since the listing is automatically updated
const route = useRoute();
const isResultPage = computed(
    () => route.path.includes('/search') && route.query.search !== undefined,
);

// Use searchStore to share searchTerm with search result page
const searchStore = useSearchStore();
// Defer the search request to prevent the search from instantly being triggered, 500ms fixes holding backspace triggering twice
const performDebouncedSearch = useDebounceFn(async (value: string) => {
    // Only perform search and update valid searchTerm if searchTerm is longer than minCharacters
    if (value.length >= props.minCharacters) {
        searchTerm.value = value;
        searchStore.updateLastValidSearchTerm(value);

        // Update URL with new valid search term
        if (isResultPage.value) {
            router.push({
                query: {
                    ...route.query,
                    search: value,
                    p: undefined,
                    properties: undefined,
                    sort: undefined,
                    filters: undefined,
                },
            });
        } else {
            await search({
                associations: {
                    vendorsAvailableSalesChannels: {},
                },
            });
            results.value = getProducts.value;
            trackSearchSuggestions();
        }
    }
}, 500);

watch(typingQuery, (value: string) => {
    // Update searchTerm in searchStore
    searchStore.updateSearchTerm(value);
    performDebouncedSearch(value);
    if( value.length === 0) {
        results.value = [];
    }
});

// Suggest results will only be shown, when the user has typed more than the minimum characters prop in the search field
const showSuggest = computed(() => typingQuery.value.length >= props.minCharacters && !isResultPage.value);

// Redirect to search result page when pressing enter
const handleEnter = () => {
    if (typingQuery.value.length >= 1) {
        trackSearch();
        navigateTo(url(`/search?search=${typingQuery.value}`));
    }

    if (isResultPage.value) {
        emit('closeSearch');
    }
};

const onClickProduct = (product: Schemas['Product']) => {
    trackSelectItem(product, { id: 'search-suggest', name: 'search-suggest' });
    emit('closeSearch');
};

const searchComponent = ref(null);
onClickOutside(searchComponent, () => {
    typingQuery.value = '';
    results.value = [];
});

onMounted(() => {
    // If on result page, set the search input to the last valid search term when opening it
    if (isResultPage.value) {
        // Get the input from the ref (need querySelector since the ref returns the FormKit wrapper and not the input itself)
        searchInput.value.$el.querySelector('input').focus();
        searchInput.value.$el.querySelector('input').value =
            searchStore.lastValidSearchTerm !== '' ? searchStore.lastValidSearchTerm : (route.query.search ?? '');
    }
});
</script>

<template>
    
<FormKitLazyProvider config-file="true">
<div
        ref="searchComponent"
        class="bg-white mt-1 md:mt-0 md:w-4/12 md:bg-neutral-300"
    >
        <FormKit
            ref="searchInput"
            v-model="typingQuery"
            type="text"
            suffix-icon="search"
            :floating-label="false"
            :classes="{
                inner: 'focus-within:ring-[0] focus-within:border-0 shadow-none border-0 gap-4 !p-0 relative md:bg-neutral-300',
                outer: 'w-full',
                input: 'border border-neutral-800 py-1 px-4 rounded-3xl h-11 placeholder:!text-neutral-550 md:focus:bg-neutral-100',
                suffixIcon: 'block h-auto !w-4 absolute right-4 top-3.5 *:fill-none',
            }"
            :placeholder="t('search.searchBar.placeholder')"
            @keyup.enter="
                () => {
                    handleEnter();
                }
            "
        />
    </div>

    <template v-if="showSuggest">
        <Teleport to="#flyouts">
            <div class="container relative">
                <div
                    class="bg-white absolute left-0 top-full z-20 w-full px-3 py-4 shadow-search md:left-[unset] md:right-6 md:w-120 md:rounded md:border md:border-neutral-400 md:px-1"
                    :class="{
                        'md:pt-0': getTotal > 0,
                    }"
                >
                    <LocaleLink
                        v-for="product in results?.slice(0, displayTotal)"
                        :key="product.id"
                        :to="getProductRoute(product)"
                        @click="onClickProduct(product)"
                    >
                        <LayoutHeaderSearchSuggestions :product="product" />
                    </LocaleLink>

                    <div class="text-center text-sm">
                        <div
                            v-if="loading"
                            class="h-1lh relative mt-4"
                        >
                            <UtilityLoadingSpinner size="small" />
                        </div>

                        <template v-else>
                            <LocaleLink
                                v-if="getTotal > 0 && results.length > 0"
                                :to="'/search?search=' + typingQuery"
                                class="block w-full pt-4"
                                @click="isResultPage = true"
                            >
                                <FormKit
                                    type="button"
                                    :classes="{
                                        input: 'font-bold',
                                    }"
                                >
                                    <template v-if="getTotal > 1">
                                        <span>{{ t('search.searchBar.resultsLinkLabel', { number: getTotal }) }}</span>
                                    </template>

                                    <template v-else>
                                        <span>{{ t('search.searchBar.oneResultLinkLabel') }}</span>
                                    </template>
                                </FormKit>
                            </LocaleLink>

                            <div
                                v-else
                                class="py-3"
                            >
                                {{ t('search.searchBar.noResultsLabel') }}
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </Teleport>
    </template>
</FormKitLazyProvider>

</template>
